import { CkeditorConstants } from "./ckeditorconstants";
import { CKEditor5 } from "../ckeditor";
import {
    stringToBoolean,
    getSelectedContent
  } from "./subtemplateutils"
const {
    htmlElement: { SPAN, FIG_CAPTION },
    htmlAttributes: { CLASS },
    helperAttributes: { CONTENTEDITABLE },
    elementClass: {
      suggestion_marker,
      LIST_CLASS
    },
    HEADING_TAG_NAMES
  } = CkeditorConstants;

/**
 * Check selection/cusrsor in document includes a figcaption
 * @param { CKEditor5 } editor
 * @returns { boolean }
 */
export const checkForFigCaption = (editor) => {
    const ancestors = editor.editing.view.document.selection.anchor.getAncestors().reverse();
    return ancestors.some((ele) => ele.name && ele.name === FIG_CAPTION);
}

/**
 * Check selection/cursor in document includes a span element
 * @param { CKEditor5 } editor
 * @returns { boolean }
 */
export const checkForSpan = (editor) => {
    const ancestors = editor.editing.view.document.selection.anchor.getAncestors().reverse();
    return ancestors.some((ele) => ele.name && ele.name === SPAN && !(ele.hasAttribute(CLASS) &&
        (ele.getAttribute(CLASS).includes(suggestion_marker) || ele.getAttribute(CLASS).includes(LIST_CLASS))));
}

/**
 * Check selection/cursor in document is in editable element
 * @param { CKEditor5 } editor
 * @returns { boolean }
 */
export const checkForEditability = (editor) => {
    const selection = editor.model.document.selection;
    const selectedElement = selection.getSelectedElement();

    // Check if the selected element or its parent has contenteditable attribute set to false
    if (selectedElement) {
        return selectedElement.hasAttribute(CONTENTEDITABLE) && !stringToBoolean(selectedElement.getAttribute(CONTENTEDITABLE));
    } else {
        const range = selection.getFirstRange();
        const parentElement = range.start.parent;
        return parentElement.hasAttribute(CONTENTEDITABLE) && !stringToBoolean(parentElement.getAttribute(CONTENTEDITABLE));
    }
}

/**
 * Check selection in document includes a Image | Video | Audio | Table
 * @param { CKEditor5 } editor
 * @returns { boolean }
 */
export const checkForMedia = (editor) => {
    const selectedHtmlString = getSelectedContent(editor);
    const regex = /<(img|audio|video|table)[^>]*\/?>/g;
    return selectedHtmlString.match(regex) ? true : false;
}

/**
 * Check selection/cusrsor in document includes a Header(h1-h6)
 * @param { CKEditor5 } editor
 * @returns { boolean }
 */
export const checkForHeader = (editor) => {
    const ancestors = editor.editing.view.document.selection.anchor.getAncestors().reverse();
    return ancestors.some((ele) => ele.name && HEADING_TAG_NAMES.includes(ele.name));
}


/**
 * Check selection/cursor in document includes only one span element
 * @param { CKEditor5 } editor
 * @returns { boolean }
 */
export const isSingleSpanSelected = (editor) => {
    const selection = editor.model.document.selection;

    const spanCount = [...selection.getRanges()]
    .flatMap(range => [...range.getItems()])
    .filter(item => item.is('element', SPAN)).length;

    const { anchor, focus } = selection;

    return (anchor.parent !== focus.parent ) || spanCount > 1 ? false : true;
};

/**
 * Check selection/cursor in document includes only white space
 * @param { CKEditor5 } editor
 * @returns { boolean }
 */
export const checkForWhiteSpace = (editor) => {
    const selection = editor.model.document.selection;
    for (const range of selection.getRanges()) {
        const walker = range.getWalker();
        let hasContent = false;

        for (const item of walker) {
            const text = item.item.data?.trim();
            // If we find non-whitespace content, we can stop early.
            if (text !== '') {
                hasContent = true;
                break;
            }
        }
        // Return true if only whitespace or no content was found in the range.
        if (!hasContent) {
            return true;
        }
    }
    return false;
};

/**
 * Check selection/cursor in document includes comments
 * @param { CKEditor5 } editor
 * @returns { boolean }
 */
export const checkSelectionIncludesComments = (editor) => {
    const selection = editor.model.document.selection;
    const markers = editor.model.markers;
    for (const range of selection.getRanges()) {
        for (const marker of markers) {
            if (marker.name.startsWith('comment:') && range.containsRange(marker.getRange(), true)) {
                return true; // The selection has a comment.
            }
        }
    }
    return false; // No comments found in the selection.
};

/**
 * Check selection/cursor in document includes in a table cell
 * @param { CKEditor5 } editor
 * @returns { boolean }
 */
export const checkForTable = (editor) => {
    const selection = editor.model.document.selection;

    // Get the position of the selection
    const position = selection.anchor;

    // Check if the position is inside a table cell
    let isInsideTable = false;
    for (const ancestor of position.getAncestors()) {
        if (ancestor.name === 'table') {
            isInsideTable = true;
            break;
        }
    }
    return isInsideTable;
}